import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import {
  ActivityAdminService,
  ActivityCreateRequest,
  ActivitySpecialMenuDay,
  ActivityStatus,
  AdminActivityItem,
} from '../core/services/activity-admin.service';
import { FormApiService } from '../core/services/formapiService';
import { LocationService } from '../core/services/location.service';
import { MealMenuAdminService, MealMenuItem } from '../core/services/meal-menu-admin.service';
import { getImageUrl } from '../shared/utils/commenFunctions';
import {
  ACTIVITY_TYPE_OPTIONS,
  domainsForActivityType,
} from '../shared/utils/activity-type-domain.map';
import {
  ACTIVITY_MONTHLY_ORDINALS,
  ACTIVITY_WEEKDAY_OPTIONS,
  ActivityMonthlyOrdinal,
  buildMonthlyOccurrence,
  parseMonthlyOccurrence,
} from '../shared/utils/activity-recurrence';
import {
  generateNextActivityCode,
  isStructuredActivityCode,
} from '../shared/utils/activity-code';

@Component({
  selector: 'activity-form-component',
  standalone: true,
  templateUrl: './activity-form.component.html',
  styleUrls: ['./activity-form.component.css'],
  imports: [CommonModule, FormsModule, RouterModule],
})
export class ActivityFormComponent implements OnInit {
  @ViewChild('fileInput') fileInput?: ElementRef<HTMLInputElement>;

  activityId: string | null = null;
  isEditMode = false;
  isLoading = false;

  activityName = '';
  /** Structured code: {YEAR}-{TYPE}-{SEQ} e.g. 2026-GAME-001 — auto-generated from Type. */
  code = '';
  type = '';
  description = '';
  limitedCapacity = '';
  center = '';
  domainsOfWellness: string[] = [];
  thumbnailFileName = '';
  thumbnailPreviewUrl = '';
  thumbnailDisplayName = '';
  isUploadingThumbnail = false;
  isGeneratingCode = false;
  private selectedThumbnailFile: File | null = null;
  private existingActivityCodes: string[] = [];
  isSaving = false;
  errorMessage: string | null = null;
  successMessage: string | null = null;

  isSpecialEvent: boolean | null = null;
  isSeries: boolean | null = null;
  /** When true, booked seniors appear in Staff Service Delivery for this activity. */
  serviceDeliveryNeeded: boolean | null = null;
  needsSpecialMenu: boolean | null = null;
  offerHotMeal = false;
  offerColdMeal = false;
  specialMenuDays: ActivitySpecialMenuDay[] = [];
  isLoadingSpecialMenus = false;
  private mealMenuByDay = new Map<string, MealMenuItem>();

  timeMode: 'all_day' | 'specific' | '' = '';
  startTime = '';
  endTime = '';
  reoccurring: 'One Time' | 'Weekly' | 'Monthly' | '' = '';
  reoccurringDays: string[] = [];
  /** Monthly series: which week of the month (1st–4th). */
  monthlyOrdinal: ActivityMonthlyOrdinal | '' = '';
  /** Monthly series: weekday short code (Mon–Fri). */
  monthlyWeekday = '';
  fromDate = '';
  thruDate = '';
  /** Which picker is focused — hides "Select Time/Dates" overlay so it can't overlap native digits. */
  focusedPicker: 'startTime' | 'endTime' | 'fromDate' | 'thruDate' | null = null;

  /** Must match Activity Screen tabs: Game / Health & Wellness / Educational */
  readonly typeOptions = [...ACTIVITY_TYPE_OPTIONS];

  /** Centers are closed Sat/Sun — weekly / monthly day picks are Mon–Fri only. */
  readonly weekDays = ACTIVITY_WEEKDAY_OPTIONS.map((d) => ({ label: d.label, value: d.value }));
  readonly monthlyOrdinals = [...ACTIVITY_MONTHLY_ORDINALS];

  readonly reoccurringOptions: Array<'One Time' | 'Weekly' | 'Monthly'> = [
    'One Time',
    'Weekly',
    'Monthly',
  ];

  centerOptions: string[] = [];

  get pageTitle(): string {
    return this.isEditMode ? 'Edit Activity' : 'Creating New Activity';
  }

  get pageSubtitle(): string {
    return this.isEditMode
      ? 'Update activity details. Changes will be reflected on the activity master list.'
      : "To create a activity, it's essential to provide comprehensive details that will be showcased on the list.";
  }

  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly activityAdminService: ActivityAdminService,
    private readonly formApiService: FormApiService,
    private readonly locationService: LocationService,
    private readonly mealMenuAdminService: MealMenuAdminService,
  ) {}

  ngOnInit(): void {
    const token = sessionStorage.getItem('token');
    if (!token) {
      void this.router.navigate(['/'], { replaceUrl: true });
      return;
    }

    this.loadCenterOptions();
    this.loadMealMenus();
    this.loadExistingActivityCodes();

    const id = this.route.snapshot.paramMap.get('activityId')?.trim() || null;
    if (id) {
      this.activityId = id;
      this.isEditMode = true;
      this.loadActivityForEdit(id);
    }
  }

  goBack(): void {
    void this.router.navigate(['/manage-services/senior-center/activity']);
  }

  /** True when code is locked to Year-Type-Seq nomenclature (create, or structured edit). */
  get isCodeAutoManaged(): boolean {
    if (!this.isEditMode) {
      return true;
    }
    return !this.code.trim() || isStructuredActivityCode(this.code);
  }

  get codeHint(): string {
    if (!this.type.trim()) {
      return 'Select Type to generate a code (Year-Type-###).';
    }
    if (this.isGeneratingCode) {
      return 'Generating next code…';
    }
    return 'Format: Year-Type-### (e.g. 2026-GAME-001). Auto-assigned from Type.';
  }

  triggerUpload(): void {
    if (this.isUploadingThumbnail || this.isSaving) {
      return;
    }
    this.fileInput?.nativeElement.click();
  }

  onThumbnailPreviewError(): void {
    this.thumbnailPreviewUrl = '';
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) {
      return;
    }

    const maxBytes = 5 * 1024 * 1024;
    if (file.size > maxBytes) {
      this.errorMessage = 'File must be 5 MB or smaller.';
      input.value = '';
      return;
    }

    this.errorMessage = null;
    this.successMessage = null;
    this.selectedThumbnailFile = file;
    this.thumbnailDisplayName = file.name;
    this.isUploadingThumbnail = true;

    if (this.thumbnailPreviewUrl && this.thumbnailPreviewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(this.thumbnailPreviewUrl);
    }

    if (file.type.startsWith('image/')) {
      this.thumbnailPreviewUrl = URL.createObjectURL(file);
    } else {
      this.thumbnailPreviewUrl = '';
    }

    // Upload immediately via existing /api/file/upload (same as program forms).
    this.uploadThumbnailViaExistingApi(file).subscribe({
      next: (blobName) => {
        this.isUploadingThumbnail = false;
        this.thumbnailFileName = blobName;
        this.selectedThumbnailFile = null;
        this.thumbnailPreviewUrl = getImageUrl(blobName);
        this.thumbnailDisplayName = file.name;
      },
      error: (err) => {
        this.isUploadingThumbnail = false;
        this.selectedThumbnailFile = null;
        this.thumbnailFileName = '';
        this.errorMessage =
          err?.error?.message ||
          err?.error?.Message ||
          err?.message ||
          'Unable to upload image. Please try again.';
      },
    });
  }

  onTypeChanged(): void {
    this.domainsOfWellness = domainsForActivityType(this.type);
    this.refreshActivityCodeFromType();
  }

  private loadExistingActivityCodes(): void {
    this.activityAdminService.listActivities({ index: 0, limit: 500 }).subscribe({
      next: (response) => {
        this.existingActivityCodes = (response.activities ?? [])
          .map((item) => (item.code ?? '').trim())
          .filter((code) => !!code);
        // Assign structured code once catalog codes are available (create, or edit with blank code).
        if (this.type.trim() && (!this.isEditMode || !this.code.trim())) {
          this.refreshActivityCodeFromType();
        }
      },
      error: () => {
        this.existingActivityCodes = [];
      },
    });
  }

  private refreshActivityCodeFromType(): void {
    // Preserve legacy free-text codes on edit; only manage structured / empty codes.
    if (this.isEditMode && this.code.trim() && !isStructuredActivityCode(this.code)) {
      return;
    }
    if (!this.type.trim()) {
      if (!this.isEditMode) {
        this.code = '';
      }
      return;
    }

    this.isGeneratingCode = true;
    const generated = generateNextActivityCode(this.type, this.existingActivityCodes);
    this.code = generated ?? '';
    this.isGeneratingCode = false;
  }

  setSpecialEvent(value: boolean): void {
    this.isSpecialEvent = value;
    // Hot/Cold special menu is independent of Special Activity — do not clear meal options.
  }

  setServiceDeliveryNeeded(value: boolean): void {
    this.serviceDeliveryNeeded = value;
  }

  setNeedsSpecialMenu(value: boolean): void {
    this.needsSpecialMenu = value;
    if (!value) {
      this.offerHotMeal = false;
      this.offerColdMeal = false;
      this.specialMenuDays = [];
      return;
    }
    // Default to both when enabling special menu until staff picks a mode.
    if (!this.offerHotMeal && !this.offerColdMeal) {
      this.offerHotMeal = true;
      this.offerColdMeal = true;
    }
    this.refreshSpecialMenusFromDates();
  }

  get specialMenuOfferMode(): 'hot_only' | 'cold_only' | 'both' | null {
    if (this.offerHotMeal && this.offerColdMeal) {
      return 'both';
    }
    if (this.offerHotMeal && !this.offerColdMeal) {
      return 'hot_only';
    }
    if (!this.offerHotMeal && this.offerColdMeal) {
      return 'cold_only';
    }
    return null;
  }

  setSpecialMenuOfferMode(mode: 'hot_only' | 'cold_only' | 'both'): void {
    if (mode === 'hot_only') {
      this.offerHotMeal = true;
      this.offerColdMeal = false;
      return;
    }
    if (mode === 'cold_only') {
      this.offerHotMeal = false;
      this.offerColdMeal = true;
      return;
    }
    this.offerHotMeal = true;
    this.offerColdMeal = true;
  }

  setOfferHotMeal(value: boolean): void {
    this.offerHotMeal = value;
  }

  setOfferColdMeal(value: boolean): void {
    this.offerColdMeal = value;
  }

  onFromDateChange(value: string): void {
    this.fromDate = value ?? '';
    if (this.reoccurring === 'One Time' && this.fromDate) {
      this.thruDate = this.fromDate;
    }
    if (this.needsSpecialMenu === true) {
      this.refreshSpecialMenusFromDates();
    }
  }

  onThruDateChange(value: string): void {
    if (this.reoccurring === 'One Time') {
      this.thruDate = this.fromDate;
      return;
    }
    this.thruDate = value ?? '';
    if (this.needsSpecialMenu === true) {
      this.refreshSpecialMenusFromDates();
    }
  }

  /** Capacity must be a whole number (no decimals). */
  onCapacityInput(value: string | number | null): void {
    const raw = value == null ? '' : String(value);
    if (!raw.trim()) {
      this.limitedCapacity = '';
      return;
    }
    // Strip decimals / non-digits so staff cannot enter values like 20.58.
    const digitsOnly = raw.replace(/[^\d]/g, '');
    this.limitedCapacity = digitsOnly;
  }

  get isOneTimeEvent(): boolean {
    return this.reoccurring === 'One Time';
  }

  setSeries(value: boolean): void {
    this.isSeries = value;
    if (value === true) {
      // Series cannot be One Time — show Weekly days immediately so staff can pick when it repeats.
      if (this.reoccurring !== 'Weekly' && this.reoccurring !== 'Monthly') {
        this.setReoccurring('Weekly');
      }
      return;
    }
    // Series No → default back to a single occurrence if they had left Weekly/Monthly empty of days.
    if (!this.reoccurring) {
      this.setReoccurring('One Time');
    }
  }

  /** One Time is only valid when Series is No. */
  get visibleReoccurringOptions(): Array<'One Time' | 'Weekly' | 'Monthly'> {
    if (this.isSeries === true) {
      return this.reoccurringOptions.filter((option) => option !== 'One Time');
    }
    return this.reoccurringOptions;
  }

  setTimeMode(value: 'all_day' | 'specific'): void {
    this.timeMode = value;
    if (value === 'all_day') {
      this.startTime = '';
      this.endTime = '';
    }
  }

  setReoccurring(value: 'One Time' | 'Weekly' | 'Monthly'): void {
    this.reoccurring = value;
    if (value !== 'Weekly') {
      this.reoccurringDays = [];
    }
    if (value !== 'Monthly') {
      this.monthlyOrdinal = '';
      this.monthlyWeekday = '';
    }
    // One-time events use the same start and end date.
    if (value === 'One Time' && this.fromDate) {
      this.thruDate = this.fromDate;
    }
  }

  isDaySelected(day: string): boolean {
    return this.reoccurringDays.includes(day);
  }

  toggleReoccurringDay(day: string): void {
    if (this.isDaySelected(day)) {
      this.reoccurringDays = this.reoccurringDays.filter((d) => d !== day);
      return;
    }
    this.reoccurringDays = [...this.reoccurringDays, day];
  }

  setMonthlyOrdinal(ordinal: ActivityMonthlyOrdinal): void {
    this.monthlyOrdinal = this.monthlyOrdinal === ordinal ? '' : ordinal;
  }

  setMonthlyWeekday(day: string): void {
    this.monthlyWeekday = this.monthlyWeekday === day ? '' : day;
  }

  get monthlyOccurrenceLabel(): string {
    return buildMonthlyOccurrence(this.monthlyOrdinal, this.monthlyWeekday) || '';
  }

  onCancel(): void {
    this.goBack();
  }

  onSaveDraft(): void {
    this.saveActivity('draft');
  }

  onSavePublish(): void {
    this.saveActivity('active');
  }

  openNativePicker(input: HTMLInputElement | undefined | null): void {
    if (!input || input.disabled) {
      return;
    }
    input.focus();
    const picker = input as HTMLInputElement & { showPicker?: () => void };
    if (typeof picker.showPicker === 'function') {
      try {
        picker.showPicker();
        return;
      } catch {
        // Fall through to click for browsers that block showPicker.
      }
    }
    input.click();
  }

  onPickerFocus(field: 'startTime' | 'endTime' | 'fromDate' | 'thruDate'): void {
    this.focusedPicker = field;
  }

  onPickerBlur(field: 'startTime' | 'endTime' | 'fromDate' | 'thruDate'): void {
    if (this.focusedPicker === field) {
      this.focusedPicker = null;
    }
  }

  showPickerPlaceholder(field: 'startTime' | 'endTime' | 'fromDate' | 'thruDate'): boolean {
    if (this.focusedPicker === field) {
      return false;
    }
    switch (field) {
      case 'startTime':
        return !this.startTime;
      case 'endTime':
        return !this.endTime;
      case 'fromDate':
        return !this.fromDate;
      case 'thruDate':
        return !this.thruDate;
      default:
        return false;
    }
  }

  private loadCenterOptions(): void {
    this.locationService.getActiveLocations().subscribe({
      next: (response) => {
        const names = (response.locations ?? [])
          .map((item) => item.locationName?.trim() ?? '')
          .filter((name) => !!name && name.toLowerCase() !== 'all');
        // One activity can apply to every center — do not require a per-center copy.
        this.centerOptions = ['All Centers', ...names];
      },
      error: () => {
        this.centerOptions = ['All Centers'];
      },
    });
  }

  private loadActivityForEdit(activityId: string): void {
    this.isLoading = true;
    this.errorMessage = null;

    this.activityAdminService.getActivity(activityId).subscribe({
      next: (response) => {
        this.isLoading = false;
        if (response.activity) {
          this.prefillFromActivity(response.activity);
        }
      },
      error: (err) => {
        this.isLoading = false;
        this.errorMessage =
          err?.error?.message ||
          err?.error?.Message ||
          'Unable to load activity for editing.';
      },
    });
  }

  private prefillFromActivity(item: AdminActivityItem): void {
    this.activityName = item.activityName?.trim() || '';
    this.code = item.code?.trim() || '';
    this.type = item.type?.trim() || item.groupName?.trim() || '';
    this.description = item.description?.trim() || '';
    this.limitedCapacity =
      item.limitedCapacity == null || Number.isNaN(Number(item.limitedCapacity))
        ? ''
        : String(item.limitedCapacity);
    const loadedCenter = item.center?.trim() || '';
    const centerLower = loadedCenter.toLowerCase();
    this.center =
      !loadedCenter || centerLower === 'all' || centerLower === 'all centers'
        ? 'All Centers'
        : loadedCenter;
    // Domains always come from Type mapping (not independently edited / imported).
    this.domainsOfWellness = domainsForActivityType(this.type);
    this.thumbnailFileName = item.activityImage?.trim() || '';
    this.selectedThumbnailFile = null;
    this.thumbnailDisplayName = this.thumbnailFileName
      ? this.thumbnailFileName.split('/').pop() || this.thumbnailFileName
      : '';
    this.thumbnailPreviewUrl = this.thumbnailFileName ? getImageUrl(this.thumbnailFileName) : '';

    this.isSpecialEvent = item.isSpecial == null ? null : !!item.isSpecial;
    this.isSeries = item.isSeries == null ? null : !!item.isSeries;
    this.serviceDeliveryNeeded =
      item.serviceDeliveryNeeded == null ? null : !!item.serviceDeliveryNeeded;

    const mode = (item.timeMode || '').trim().toLowerCase();
    this.timeMode = mode === 'all_day' || mode === 'specific' ? mode : '';
    this.startTime = item.startTime?.trim() || '';
    this.endTime = item.endTime?.trim() || '';

    const recur = (item.reoccurring || '').trim();
    this.reoccurring =
      recur === 'One Time' || recur === 'Weekly' || recur === 'Monthly' ? recur : '';

    const storedDays = Array.isArray(item.reoccurringDays)
      ? item.reoccurringDays.map((d) => String(d).trim()).filter(Boolean)
      : [];

    this.reoccurringDays = [];
    this.monthlyOrdinal = '';
    this.monthlyWeekday = '';

    if (this.reoccurring === 'Weekly') {
      this.reoccurringDays = storedDays.filter((d) =>
        this.weekDays.some((day) => day.value === d),
      );
    } else if (this.reoccurring === 'Monthly') {
      const parsed =
        storedDays.map((d) => parseMonthlyOccurrence(d)).find((p) => p != null) ?? null;
      if (parsed) {
        this.monthlyOrdinal = parsed.ordinal;
        this.monthlyWeekday = parsed.weekday;
      }
    }

    this.fromDate = this.toDateInputValue(item.fromDate);
    this.thruDate = this.toDateInputValue(item.thruDate);

    this.needsSpecialMenu =
      item.needsSpecialMenu == null ? null : !!item.needsSpecialMenu;
    this.offerHotMeal = !!item.offerHotMeal;
    this.offerColdMeal = !!item.offerColdMeal;
    if (this.needsSpecialMenu === true && !this.offerHotMeal && !this.offerColdMeal) {
      this.offerHotMeal = true;
      this.offerColdMeal = true;
    }
    this.specialMenuDays = Array.isArray(item.specialMenuDays)
      ? item.specialMenuDays.map((day) => ({
          date: day.date,
          day: day.day,
          hotMeal: day.hotMeal ?? '',
          coldMeal: day.coldMeal ?? '',
          specialEvents: day.specialEvents ?? '',
        }))
      : [];

    if (this.isSpecialEvent && this.needsSpecialMenu && !this.specialMenuDays.length) {
      this.refreshSpecialMenusFromDates();
    }
  }

  private toDateInputValue(value: string | null | undefined): string {
    if (!value) {
      return '';
    }
    const raw = String(value).trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
      return raw.slice(0, 10);
    }
    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) {
      return '';
    }
    const yyyy = parsed.getFullYear();
    const mm = String(parsed.getMonth() + 1).padStart(2, '0');
    const dd = String(parsed.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  private saveActivity(status: ActivityStatus): void {
    if (this.isSaving) {
      return;
    }

    const name = this.activityName.trim();
    if (!name) {
      this.errorMessage = 'Activity name is required.';
      this.successMessage = null;
      return;
    }

    const selectedType = this.type.trim();
    if (!selectedType) {
      this.errorMessage = 'Type is required. Domains of Wellness are set automatically from Type.';
      this.successMessage = null;
      return;
    }

    // Structured nomenclature: create always gets Year-Type-###; edit regenerates only if Type prefix changed.
    if (!this.isEditMode || !this.code.trim() || isStructuredActivityCode(this.code)) {
      const generated = generateNextActivityCode(selectedType, this.existingActivityCodes);
      if (!generated) {
        this.errorMessage = 'Unable to generate activity code. Select a valid Type.';
        this.successMessage = null;
        return;
      }
      if (this.isEditMode && isStructuredActivityCode(this.code)) {
        const currentPrefix = this.code.trim().toUpperCase().replace(/-\d+$/, '');
        const nextPrefix = generated.replace(/-\d+$/, '').toUpperCase();
        if (currentPrefix !== nextPrefix) {
          this.code = generated;
        }
      } else if (!this.code.trim() || !this.isEditMode) {
        this.code = generated;
      }
    }

    if (!this.code.trim()) {
      this.errorMessage = 'Activity code is required.';
      this.successMessage = null;
      return;
    }

    const mappedDomains = domainsForActivityType(selectedType);
    if (mappedDomains.length === 0) {
      this.errorMessage = 'Select a valid Type so Domains of Wellness can be set.';
      this.successMessage = null;
      return;
    }
    this.domainsOfWellness = mappedDomains;

    const capacityRaw = this.limitedCapacity?.toString().trim();
    let limitedCapacity: number | null = null;
    if (capacityRaw) {
      if (!/^\d+$/.test(capacityRaw)) {
        this.errorMessage = 'Limited capacity must be a whole number (no decimals).';
        this.successMessage = null;
        return;
      }
      const parsed = Number(capacityRaw);
      if (!Number.isFinite(parsed) || parsed < 0 || !Number.isInteger(parsed)) {
        this.errorMessage = 'Limited capacity must be a whole number (no decimals).';
        this.successMessage = null;
        return;
      }
      limitedCapacity = parsed;
    }

    if (this.timeMode === 'specific' && this.startTime && this.endTime && this.startTime > this.endTime) {
      this.errorMessage = 'End time must be after start time.';
      this.successMessage = null;
      return;
    }

    // One-time events must have the same start and end date.
    if (this.reoccurring === 'One Time' && this.fromDate) {
      this.thruDate = this.fromDate;
    }

    if (this.fromDate && this.thruDate && this.fromDate > this.thruDate) {
      this.errorMessage = 'Thru date must be on or after From date.';
      this.successMessage = null;
      return;
    }

    if (this.serviceDeliveryNeeded === null) {
      this.errorMessage = 'Select whether Service Delivery is needed for this activity.';
      this.successMessage = null;
      return;
    }

    if (this.isSpecialEvent === true && !this.fromDate) {
      this.errorMessage = 'From Date is required for Special Activities so they appear in Upcoming Events.';
      this.successMessage = null;
      return;
    }

    if (this.needsSpecialMenu === true && !this.offerHotMeal && !this.offerColdMeal) {
      this.errorMessage = 'Select Hot Meal Only, Cold Meal Only, or Cold and Hot Meal.';
      this.successMessage = null;
      return;
    }

    if (this.isUploadingThumbnail) {
      this.errorMessage = 'Please wait for the image upload to finish.';
      this.successMessage = null;
      return;
    }

    if (
      this.isSeries === true &&
      this.reoccurring !== 'Weekly' &&
      this.reoccurring !== 'Monthly'
    ) {
      this.errorMessage =
        'For a series, select Weekly or Monthly and when it happens (days / monthly occurrence).';
      this.successMessage = null;
      return;
    }

    if (this.reoccurring === 'Weekly' && this.reoccurringDays.length === 0) {
      this.errorMessage =
        'Select at least one reoccurring day (Monday–Friday). Centers are closed Saturday and Sunday.';
      this.successMessage = null;
      return;
    }

    const monthlyOccurrence =
      this.reoccurring === 'Monthly'
        ? buildMonthlyOccurrence(this.monthlyOrdinal, this.monthlyWeekday)
        : null;
    if (this.reoccurring === 'Monthly' && !monthlyOccurrence) {
      this.errorMessage =
        'Select when this monthly series happens (e.g. 1st Monday, 2nd Tuesday). Monday–Friday only.';
      this.successMessage = null;
      return;
    }

    const payload: ActivityCreateRequest = {
      activityName: name,
      code: this.code.trim() || undefined,
      type: selectedType,
      description: this.description.trim() || undefined,
      domainsOfWellness: mappedDomains,
      limitedCapacity,
      // Empty / All Centers → backend treats as available at every location.
      center: (() => {
        const c = this.center.trim();
        if (!c || c.toLowerCase() === 'all' || c.toLowerCase() === 'all centers') {
          return undefined;
        }
        return c;
      })(),
      activityImage: this.thumbnailFileName || undefined,
      isSpecial: this.isSpecialEvent === null ? undefined : this.isSpecialEvent,
      isSeries: this.isSeries === null ? undefined : this.isSeries,
      serviceDeliveryNeeded: this.serviceDeliveryNeeded === true,
      reoccurring: this.reoccurring || undefined,
      timeMode: this.timeMode || undefined,
      startTime: this.timeMode === 'specific' ? this.startTime || undefined : undefined,
      endTime: this.timeMode === 'specific' ? this.endTime || undefined : undefined,
      reoccurringDays:
        this.reoccurring === 'Weekly' && this.reoccurringDays.length
          ? this.reoccurringDays.filter((d) => this.weekDays.some((day) => day.value === d))
          : this.reoccurring === 'Monthly' && monthlyOccurrence
            ? [monthlyOccurrence]
            : undefined,
      fromDate: this.fromDate || undefined,
      thruDate: this.thruDate || undefined,
      needsSpecialMenu: this.needsSpecialMenu === true,
      offerHotMeal: this.needsSpecialMenu === true ? this.offerHotMeal : false,
      offerColdMeal: this.needsSpecialMenu === true ? this.offerColdMeal : false,
      specialMenuDays:
        this.needsSpecialMenu === true ? this.specialMenuDays : undefined,
      status,
    };

    this.isSaving = true;
    this.errorMessage = null;
    this.successMessage = null;

    this.resolveActivityImageBlobName()
      .pipe(
        switchMap((blobName) => {
          if (blobName) {
            payload.activityImage = blobName;
            this.thumbnailFileName = blobName;
          }
          return this.isEditMode && this.activityId
            ? this.activityAdminService.updateActivity(this.activityId, payload)
            : this.activityAdminService.createActivity(payload);
        }),
      )
      .subscribe({
        next: (response) => {
          this.isSaving = false;
          this.selectedThumbnailFile = null;
          this.successMessage =
            response.message ||
            (status === 'draft'
              ? 'Activity saved as draft.'
              : this.isEditMode
                ? 'Activity updated successfully.'
                : 'Activity saved as active successfully.');
          setTimeout(() => this.goBack(), 400);
        },
        error: (err) => {
          this.isSaving = false;
          this.errorMessage =
            err?.error?.message ||
            err?.error?.Message ||
            'Unable to save activity. Please try again.';
        },
      });
  }

  /** Upload via existing FormApiService → POST /api/file/upload */
  private uploadThumbnailViaExistingApi(file: File): Observable<string> {
    return this.formApiService.uploadFile(file).pipe(
      map((response: unknown) => {
        const blobName = this.extractBlobName(response);
        if (!blobName) {
          throw new Error('File upload did not return a blob name.');
        }
        return blobName;
      }),
    );
  }

  private extractBlobName(response: unknown): string {
    if (!response) {
      return '';
    }
    if (typeof response === 'string') {
      return response.trim();
    }
    const body = response as Record<string, unknown>;
    const nested = (body['data'] as Record<string, unknown> | undefined) ?? undefined;
    const candidates = [
      body['blobName'],
      body['BlobName'],
      body['blob_name'],
      body['key'],
      body['fileKey'],
      body['id'],
      nested?.['blobName'],
      nested?.['blob_name'],
      nested?.['key'],
    ];
    for (const value of candidates) {
      if (typeof value === 'string' && value.trim()) {
        return value.trim();
      }
    }
    return '';
  }

  /** Reuse already-uploaded blob name (upload happens on file select). */
  private resolveActivityImageBlobName(): Observable<string | undefined> {
    if (this.selectedThumbnailFile) {
      return this.uploadThumbnailViaExistingApi(this.selectedThumbnailFile);
    }
    return of(this.thumbnailFileName.trim() || undefined);
  }

  private loadMealMenus(): void {
    this.mealMenuAdminService.listMenus().subscribe({
      next: (response) => {
        this.mealMenuByDay.clear();
        for (const menu of response.menus ?? []) {
          if (menu.day) {
            this.mealMenuByDay.set(menu.day.trim().toLowerCase(), menu);
          }
        }
        if (this.needsSpecialMenu === true) {
          this.refreshSpecialMenusFromDates();
        }
      },
      error: () => {
        this.mealMenuByDay.clear();
      },
    });
  }

  private refreshSpecialMenusFromDates(): void {
    if (!this.fromDate) {
      this.specialMenuDays = [];
      return;
    }

    this.isLoadingSpecialMenus = true;
    const dates = this.expandDateRange(this.fromDate, this.thruDate || this.fromDate);
    this.specialMenuDays = dates.map((date) => {
      const dayName = this.weekdayNameFromDate(date);
      const menu = this.mealMenuByDay.get(dayName.toLowerCase());
      return {
        date,
        day: dayName,
        hotMeal: menu?.hotMeal ?? '',
        coldMeal: menu?.coldMeal ?? '',
        specialEvents: menu?.specialEvents ?? '',
      };
    });
    this.isLoadingSpecialMenus = false;
  }

  private expandDateRange(from: string, thru: string): string[] {
    const start = new Date(`${from}T00:00:00`);
    const end = new Date(`${thru}T00:00:00`);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) {
      return [from];
    }

    const dates: string[] = [];
    const cursor = new Date(start);
    let guard = 0;
    while (cursor <= end && guard < 62) {
      dates.push(this.formatDateYmd(cursor));
      cursor.setDate(cursor.getDate() + 1);
      guard += 1;
    }
    return dates.length ? dates : [from];
  }

  private weekdayNameFromDate(ymd: string): string {
    const parsed = new Date(`${ymd}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) {
      return '';
    }
    return parsed.toLocaleDateString('en-US', { weekday: 'long' });
  }

  private formatDateYmd(value: Date): string {
    const yyyy = value.getFullYear();
    const mm = String(value.getMonth() + 1).padStart(2, '0');
    const dd = String(value.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }
}
